import { useState, useEffect } from 'react';
import { Chip, Button, Input, CoordioDropdown } from '@coordio/ui';
import { validateCronExpression, describeCronExpression } from '../../utils/cronValidation';

interface CronBuilderProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

const MINUTE_OPTIONS = [
  { value: '0', label: '0 (at the hour)' },
  { value: '*/5', label: 'Every 5 minutes' },
  { value: '*/15', label: 'Every 15 minutes' },
  { value: '*/30', label: 'Every 30 minutes' },
  { value: '*', label: 'Every minute' },
  { value: 'custom', label: 'Custom...' },
];

const HOUR_OPTIONS = [
  { value: '0', label: '0 (midnight)' },
  { value: '6', label: '6 (6 AM)' },
  { value: '9', label: '9 (9 AM)' },
  { value: '12', label: '12 (noon)' },
  { value: '18', label: '18 (6 PM)' },
  { value: '*/2', label: 'Every 2 hours' },
  { value: '*/6', label: 'Every 6 hours' },
  { value: '*/12', label: 'Every 12 hours' },
  { value: '*', label: 'Every hour' },
  { value: 'custom', label: 'Custom...' },
];

const DAY_OF_MONTH_OPTIONS = [
  { value: '*', label: 'Every day' },
  { value: '1', label: '1st of month' },
  { value: '15', label: '15th of month' },
  { value: '?', label: 'Any day (use with day-of-week)' },
  { value: 'custom', label: 'Custom...' },
];

const MONTH_OPTIONS = [
  { value: '*', label: 'Every month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
  { value: 'custom', label: 'Custom...' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: '*', label: 'Every day' },
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '1-5', label: 'Weekdays (Mon-Fri)' },
  { value: '0,6', label: 'Weekends (Sat-Sun)' },
  { value: '?', label: 'Any day (use with day-of-month)' },
  { value: 'custom', label: 'Custom...' },
];

/**
 * Visual cron expression builder component.
 * Allows users to build cron expressions using a visual interface.
 */
export const CronBuilder = ({ value, onChange, error, helperText }: CronBuilderProps) => {
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('0');
  const [dayOfMonth, setDayOfMonth] = useState('*');
  const [month, setMonth] = useState('*');
  const [dayOfWeek, setDayOfWeek] = useState('*');
  const [customMode, setCustomMode] = useState(false);
  const [customExpression, setCustomExpression] = useState('');

  // Parse existing cron expression on mount or when value changes
  useEffect(() => {
    if (value && validateCronExpression(value)) {
      const parts = value.trim().split(/\s+/);
      if (parts.length === 5) {
        setMinute(parts[0]);
        setHour(parts[1]);
        setDayOfMonth(parts[2]);
        setMonth(parts[3]);
        setDayOfWeek(parts[4]);
        setCustomExpression(value);
      }
    }
  }, [value]);

  // Update cron expression when fields change
  useEffect(() => {
    if (!customMode) {
      const expression = `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
      if (expression !== customExpression) {
        setCustomExpression(expression);
        onChange(expression);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minute, hour, dayOfMonth, month, dayOfWeek, customMode]);

  const handleCustomChange = (newValue: string) => {
    setCustomExpression(newValue);
    if (validateCronExpression(newValue)) {
      onChange(newValue);
    }
  };

  const handlePreset = (preset: string) => {
    setCustomMode(false);
    setCustomExpression(preset);
    onChange(preset);
    const parts = preset.split(/\s+/);
    if (parts.length === 5) {
      setMinute(parts[0]);
      setHour(parts[1]);
      setDayOfMonth(parts[2]);
      setMonth(parts[3]);
      setDayOfWeek(parts[4]);
    }
  };

  const presets = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every day at noon', value: '0 12 * * *' },
    { label: 'Every week (Sunday midnight)', value: '0 0 * * 0' },
    { label: 'Every month (1st at midnight)', value: '0 0 1 * *' },
    { label: 'Every weekday at 9 AM', value: '0 9 * * 1-5' },
    { label: 'Every 5 minutes', value: '*/5 * * * *' },
    { label: 'Every 30 minutes', value: '*/30 * * * *' },
    { label: 'Every 2 hours', value: '0 */2 * * *' },
  ];

  const dropdownValue = (current: string, options: { value: string }[]) =>
    options.some((o) => o.value === current) ? current : 'custom';

  return (
    <div className="rounded-lg border border-border bg-card p-4 mt-2">
      <div className="flex justify-between items-center mb-4">
        <h6 className="text-lg font-semibold">Cron Expression Builder</h6>
        <Button
          variant={customMode ? 'outline' : 'ghost'}
          size="sm"
          onClick={() => setCustomMode(!customMode)}
        >
          {customMode ? 'Use Builder' : 'Custom Expression'}
        </Button>
      </div>

      {customMode ? (
        <div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="cron-custom">Cron Expression</label>
            <Input
              id="cron-custom"
              className={`w-full ${
                error || (customExpression !== '' && !validateCronExpression(customExpression))
                  ? 'border-red-500'
                  : ''
              }`}
              value={customExpression}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="0 0 * * *"
            />
            <p className={`text-xs ${
              customExpression && !validateCronExpression(customExpression)
                ? 'text-red-500'
                : 'text-muted-foreground'
            }`}>
              {helperText ||
                (customExpression && !validateCronExpression(customExpression)
                  ? 'Invalid cron expression'
                  : customExpression && describeCronExpression(customExpression))}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-sm font-medium mb-2">
              Quick Presets
            </p>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <Chip
                  key={preset.value}
                  label={preset.label}
                  onClick={() => handlePreset(preset.value)}
                  variant={value === preset.value ? 'filled' : 'outlined'}
                  color={value === preset.value ? 'accent' : 'default'}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Minute</label>
              <CoordioDropdown
                options={MINUTE_OPTIONS}
                value={dropdownValue(minute, MINUTE_OPTIONS)}
                onChange={(val) => setMinute(val ?? '0')}
                clearable={false}
                size="sm"
              />
              {(minute === 'custom' || !MINUTE_OPTIONS.some((o) => o.value === minute && o.value !== 'custom')) && (
                <Input
                  className="w-full mt-2"
                  value={minute === 'custom' ? '' : minute}
                  onChange={(e) => setMinute(e.target.value)}
                  placeholder="0-59 or */n"
                />
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Hour</label>
              <CoordioDropdown
                options={HOUR_OPTIONS}
                value={dropdownValue(hour, HOUR_OPTIONS)}
                onChange={(val) => setHour(val ?? '0')}
                clearable={false}
                size="sm"
              />
              {(hour === 'custom' || !HOUR_OPTIONS.some((o) => o.value === hour && o.value !== 'custom')) && (
                <Input
                  className="w-full mt-2"
                  value={hour === 'custom' ? '' : hour}
                  onChange={(e) => setHour(e.target.value)}
                  placeholder="0-23 or */n"
                />
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Day of Month</label>
              <CoordioDropdown
                options={DAY_OF_MONTH_OPTIONS}
                value={dropdownValue(dayOfMonth, DAY_OF_MONTH_OPTIONS)}
                onChange={(val) => setDayOfMonth(val ?? '*')}
                clearable={false}
                size="sm"
              />
              {(dayOfMonth === 'custom' || !DAY_OF_MONTH_OPTIONS.some((o) => o.value === dayOfMonth && o.value !== 'custom')) && (
                <Input
                  className="w-full mt-2"
                  value={dayOfMonth === 'custom' ? '' : dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  placeholder="1-31, */n, or ?"
                />
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Month</label>
              <CoordioDropdown
                options={MONTH_OPTIONS}
                value={dropdownValue(month, MONTH_OPTIONS)}
                onChange={(val) => setMonth(val ?? '*')}
                clearable={false}
                size="sm"
              />
              {(month === 'custom' || !MONTH_OPTIONS.some((o) => o.value === month && o.value !== 'custom')) && (
                <Input
                  className="w-full mt-2"
                  value={month === 'custom' ? '' : month}
                  onChange={(e) => setMonth(e.target.value)}
                  placeholder="1-12, JAN-DEC, or */n"
                />
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Day of Week</label>
              <CoordioDropdown
                options={DAY_OF_WEEK_OPTIONS}
                value={dropdownValue(dayOfWeek, DAY_OF_WEEK_OPTIONS)}
                onChange={(val) => setDayOfWeek(val ?? '*')}
                clearable={false}
                size="sm"
              />
              {(dayOfWeek === 'custom' || !DAY_OF_WEEK_OPTIONS.some((o) => o.value === dayOfWeek && o.value !== 'custom')) && (
                <Input
                  className="w-full mt-2"
                  value={dayOfWeek === 'custom' ? '' : dayOfWeek}
                  onChange={(e) => setDayOfWeek(e.target.value)}
                  placeholder="0-7, SUN-SAT, or ?"
                />
              )}
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              <strong>Expression:</strong> {customExpression}
            </p>
            {customExpression && validateCronExpression(customExpression) && (
              <p className="text-sm text-muted-foreground mt-1">
                <strong>Description:</strong> {describeCronExpression(customExpression)}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
